import React, { useState, useEffect } from 'react';
import Core from '../../../common/clientSdk';
import Utils from '../../../common/utils';
import { ShimmerBox, FallBackBtn, PrimaryCta } from '../../../components';
import ContentCard from './ContentCard';
import UGCContentCard from './UGCContentCard';

const DEFAULT_PROFILE_PIC = "/assets/images/default-profile.png";

const buildInfluencerProfilePictureUrl = (pictureValue, influencerId) => {
    if (!pictureValue || typeof pictureValue !== 'string') return null;
    if (pictureValue.startsWith('http')) {
        return pictureValue;
    }
    if (influencerId) {
        return Utils.generateInfluencerDpUrl(influencerId, pictureValue);
    }
    return null;
};

const resolveInfluencerProfilePicture = (influencerData, influencerId) => {
    if (!influencerData) return null;
    const candidates = [influencerData.profile_picture, influencerData.influencer_profile_picture];
    for (const candidate of candidates) {
        const resolved = buildInfluencerProfilePictureUrl(candidate, influencerId);
        if (resolved) {
            return resolved;
        }
    }
    return null;
};

const resolveAvatarFromPayload = (item, influencerId) => {
    const influencerData = item?.influencer_data || {};
    return (
        resolveInfluencerProfilePicture(influencerData, influencerId) ||
        buildInfluencerProfilePictureUrl(item?.influencer_profile_picture, influencerId) ||
        buildInfluencerProfilePictureUrl(item?.profile_picture, influencerId) ||
        DEFAULT_PROFILE_PIC
    );
};


/**
 * ContentSection - Content grid with platform filtering
 * @param {object} sectionRef - Ref for scroll navigation
 * @param {string} campaignId - Campaign ID to fetch content data
 * @param {string} platformFilter - Currently selected platform filter
 * @param {function} onPlatformFilterChange - Platform filter change handler
 */
const ContentSection = ({
    sectionRef,
    campaignId,

    onDataCount,
    isVisible,
    is_public,
    shareKey,
    campaignDetail, // Received from parent
    isReportGenerated = true,
    influencerList = [], // Received from parent (assignmentInfluencerList)
    campaignType, // Received from parent for shared views
    isSharedView,
    isInitialLoading = false,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [contentList, setContentList] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const PAGE_SIZE = 8;
    // Use campaignType prop if available (for shared views), otherwise derive from campaignDetail
    const isContentCampaign = campaignType === 'content' || campaignDetail?.campaign_data?.type === 'content';

    const contentGridLoading = isInitialLoading || isLoading;

    // Fetch content list on mount or when campaignId/shareKey/campaignType changes
    useEffect(() => {
        // reset pagination when campaign changes
        setContentList([]);
        setPage(1);
        setHasMore(true);

        // For shared views, wait for campaignType to be determined before fetching
        if (is_public && shareKey && campaignType === undefined) {
            return;
        }

        if (is_public && shareKey) {
            fetchContentList(1);
        } else if (isInitialLoading) {
            setIsLoading(true);
        } else if (campaignId && influencerList?.length > 0) {
            // Private View: Use prop data
            processInfluencerList();
        } else if (campaignId && !is_public) {
            setContentList([]);
            setIsLoading(false);
            setHasMore(false);
        }
    }, [campaignId, shareKey, is_public, campaignType, isContentCampaign, influencerList, isInitialLoading]);

    // Process provided influencerList into contentList (for private view)
    const processInfluencerList = () => {
        const mappedData = [];
        (influencerList || []).forEach((item, itemIndex) => {
            const contentAnalytics = item?.content_analytics || {};
            const deepData = item?.contents_deep_data || {};
            const influencer = item?.influencer_data || {};
            const influencerId = influencer?.influencer_id;
            const assignmentId = item?.assignment_data?.assignment_id;
            const platform = Array.isArray(item?.platforms_data) ? item?.platforms_data?.[0]?.identifier_id : item?.platforms_data?.identifier_id;

            const profilePictureUrl = resolveAvatarFromPayload(item, influencerId);

            // Collect all unique content keys
            const contentKeys = new Set([
                ...Object.keys(contentAnalytics),
                ...Object.keys(deepData)
            ]);

            contentKeys.forEach((key, keyIndex) => {
                const analytics = contentAnalytics[key] || {};
                const deep = deepData[key] || {};
                const content = deep.content_data;

                // For private view we expect content to be present or at least analytics
                // If content_data is missing (e.g. only in analytics), we might miss some fields like url

                mappedData.push({
                    ...item,
                    ...analytics, // Spread first so specific overrides below take precedence

                    id: `${itemIndex}-${keyIndex}`,
                    content_id: key,
                    content_type_id: deep.content_type_id || analytics.content_type_id || content?.content_type_id,
                    contenttypeid: deep.content_type_id || analytics.content_type_id || content?.content_type_id,
                    assignmentId: assignmentId,

                    imageSource: content?.media_id,
                    thumbnail: analytics?.thumbnail || content?.media_id?.startsWith('http')
                        ? content?.media_id
                        : (content?.thumbnail_url || deep?.thumbnail_url || "/assets/images/no-img-found.webp"),

                    url: content?.url,
                    videoUrl: content?.url,

                    name: influencer?.name || "Influencer",
                    influencerUsername: influencer?.name,
                    profilePic: profilePictureUrl,
                    profilePictureUrl: profilePictureUrl,
                    profile_pic: influencer?.profile_picture,
                    influencerId: influencerId,
                    influencerDp: influencer?.profile_picture,
                    influencerHandle: platform,
                    identifier_id: platform,
                    platform_id: Array.isArray(item?.platforms_data) ? item?.platforms_data?.[0]?.platform_id : item?.platforms_data?.platform_id,
                    platformId: Array.isArray(item?.platforms_data) ? item?.platforms_data?.[0]?.platform_id : item?.platforms_data?.platform_id,
                    shortcode: analytics?.shortcode || content?.shortcode,

                    campaignId: campaignId,
                    date: item?.assignment_data?.date_added ? new Date(item.assignment_data.date_added * 1000).toLocaleDateString() : '',
                    type: item?.campaign_data?.type,

                    views: analytics.view_count || analytics.views || 0,
                    view_count: analytics.view_count || analytics.views || 0,
                    likes: analytics.like_count || 0,
                    like_count: analytics.like_count || 0,
                    comments: analytics.comment_count || 0,
                    comment_count: analytics.comment_count || 0,
                    engagementRate: analytics.engagement_rate || 0,
                    caption: analytics.caption,
                    hash_tags: analytics.hash_tags,
                });
            });
        });

        setContentList(mappedData);
        setIsLoading(false);
        setHasMore(false); // Prop data is all-at-once
    };

    // Update parent about the data count whenever contentList changes
    useEffect(() => {
        if (onDataCount) {
            onDataCount(contentList.length);
        }
    }, [contentList, onDataCount]);

    const fetchContentList = (requestedPage = 1) => {
        console.log('ContentSection - fetchContentList called:', {
            campaignId,
            shareKey,
            is_public,
            isContentCampaign,
            campaignType,
            campaignDetailType: campaignDetail?.campaign_data?.type,
            requestedPage
        });

        if (!campaignId && !shareKey) return;

        // Only fetch if public, otherwise we use props
        if (!is_public) return;

        if (requestedPage === 1) {
            setIsLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        if (is_public && shareKey) {
            // For public UGC/Content campaigns, use getPublicAssignmentInfluencerList
            if (isContentCampaign) {
                const params = { share_key: shareKey };
                console.log('ContentSection - Fetching public UGC content with params:', params, 'isContentCampaign:', isContentCampaign);
                Core.getPublicAssignmentInfluencerList(
                    (response) => {
                        console.log('ContentSection - Public UGC API Full Response:', response);
                        const assignmentList = response?.response || response?.data || [];
                        console.log('ContentSection - Assignment list length:', assignmentList.length);

                        // Map UGC data structure - iterate through contents_deep_data
                        // Only include items that have content (non-empty contents_deep_data)
                        const mappedData = [];
                        assignmentList.forEach((item, itemIndex) => {
                            const contentKeys = Object.keys(item?.contents_deep_data || {});
                            console.log(`ContentSection - Item ${itemIndex} has ${contentKeys.length} content keys:`, contentKeys);

                            // Skip assignments without content
                            if (contentKeys.length === 0) {
                                console.log(`ContentSection - Skipping item ${itemIndex} - no content`);
                                return;
                            }

                            const influencer = item?.influencer_data || {};
                            const influencerId = influencer?.influencer_id;
                            const assignmentId = item?.assignment_data?.assignment_id;
                            const platform = item?.platforms_data?.[0]?.identifier_id;

                            const profilePictureUrl = resolveAvatarFromPayload(item, influencerId);

                            contentKeys.forEach((key, keyIndex) => {
                                const content = item?.contents_deep_data?.[key]?.content_data;
                                console.log(`ContentSection - Content for key ${key}:`, content);
                                if (!content) return;

                                mappedData.push({
                                    ...item,
                                    ...(item?.content_analytics?.[key] || {}), // Spread first
                                    id: `${itemIndex}-${keyIndex}`,
                                    content_id: content?.content_id,
                                    content_type_id: content?.content_type_id,
                                    contenttypeid: content?.content_type_id,
                                    assignmentId: assignmentId,
                                    imageSource: content?.media_id,
                                    thumbnail: content?.thumbnail_url || (content?.media_id?.startsWith('http') ? content.media_id : "/assets/images/no-img-found.webp"),
                                    url: content?.url,
                                    videoUrl: content?.url,
                                    name: influencer?.name || "Influencer",
                                    influencerUsername: influencer?.name,
                                    profilePic: profilePictureUrl,
                                    profilePictureUrl: profilePictureUrl,
                                    profile_pic: influencer?.profile_picture || influencer?.influencer_profile_picture,
                                    influencerId: influencerId,
                                    influencerDp: influencer?.profile_picture || influencer?.influencer_profile_picture,
                                    influencerHandle: platform,
                                    identifier_id: item?.platforms_data?.[0]?.identifier_id,
                                    platform_id: item?.platforms_data?.[0]?.platform_id,
                                    platformId: item?.platforms_data?.[0]?.platform_id,
                                    // Analytics overrides below
                                    views: item?.content_analytics?.[key]?.view_count || 0,
                                    likes: item?.content_analytics?.[key]?.like_count || 0,
                                    comments: item?.content_analytics?.[key]?.comment_count || 0,
                                });
                            });
                        });

                        console.log('ContentSection - Final mapped data count:', mappedData.length);
                        setContentList(mappedData);
                        setHasMore(false);
                        setIsLoading(false);
                        setIsLoadingMore(false);
                    },
                    (error) => {
                        console.error('ContentSection - Public UGC API Error:', error);
                        setContentList([]);
                        setIsLoading(false);
                        setIsLoadingMore(false);
                        setHasMore(false);
                    },
                    () => { },
                    params
                );
            } else {
                // For public social campaigns, use getPublicContentSyncedList
                const params = { share_key: shareKey, start: (requestedPage - 1) * PAGE_SIZE, limit: PAGE_SIZE };
                Core.getPublicContentSyncedList(
                    (response) => {
                        console.log('ContentSection - Public API Response (page', requestedPage + '):', response);
                        const data = response?.response || response?.data || [];

                        // Map public data structure to match expected format
                        const mappedData = data.map((item, index) => {
                            const contentData = Object.values(item?.contents_deep_data || {})[0]?.content_data || {};
                            const analytics = Object.values(item?.content_analytics || {})[0] || {};

                            return {
                                ...item,
                                ...analytics,

                                id: index,
                                content_id: contentData?.content_id,
                                thumbnail: analytics?.thumbnail || (contentData?.media_id?.startsWith('http')
                                    ? contentData.media_id
                                    : (contentData?.thumbnail_url || "/assets/images/no-img-found.webp")),
                                name: item?.influencer_data?.name || "Influencer",
                                profilePic: item?.influencer_data?.profile_picture || "/assets/images/default-profile.png",
                                contenttypeid: contentData?.content_type_id,
                                views: analytics?.view_count || analytics?.views || 0,
                                view_count: analytics?.view_count || analytics?.views || 0,
                                likes: analytics?.like_count || 0,
                                like_count: analytics?.like_count || 0,
                                comments: analytics?.comment_count || 0,
                                comment_count: analytics?.comment_count || 0,
                                engagementRate: analytics?.views > 0 ? ((analytics?.like_count + analytics?.comment_count) / analytics?.views) * 100 : 0,
                                platformId: item?.platforms_data?.platform_id,
                                url: contentData?.url,
                            };
                        });

                        if (requestedPage === 1) {
                            setContentList(mappedData);
                        } else {
                            setContentList((prev) => [...prev, ...mappedData]);
                        }

                        if ((data || []).length < PAGE_SIZE) {
                            setHasMore(false);
                        } else {
                            setHasMore(true);
                        }

                        setPage((prev) => Math.max(prev, requestedPage) + 1);
                        setIsLoading(false);
                        setIsLoadingMore(false);
                    },
                    (error) => {
                        console.error('ContentSection - Public API Error:', error);
                        if (requestedPage === 1) {
                            setContentList([]);
                        }
                        setIsLoading(false);
                        setIsLoadingMore(false);
                        setHasMore(false);
                    },
                    () => { },
                    params
                );
            }

        } else if (isContentCampaign) {
            // UGC/Content campaigns: Use getAssignmentUser API with status="COMPLETED"
            const params = {
                campaign_id: campaignId,
                status: "COMPLETED"
            };
            Core.getAssignmentUser(
                (response) => {
                    console.log('ContentSection - UGC API Response:', response);
                    const assignmentList = response?.assignment_list || [];

                    // Map UGC data structure - iterate through contents_deep_data
                    const mappedData = [];
                    assignmentList.forEach((item, itemIndex) => {
                        const contentKeys = Object.keys(item?.contents_deep_data || {});
                        const influencer = item?.influencer_data || {};
                        const influencerId = influencer?.influencer_id;
                        const assignmentId = item?.assignment_data?.assignment_id;
                        const platform = item?.platforms_data?.[0]?.identifier_id;

                        const profilePictureUrl = resolveAvatarFromPayload(item, influencerId);

                        contentKeys.forEach((key, keyIndex) => {
                            const content = item?.contents_deep_data?.[key]?.content_data;
                            if (!content) return;

                            mappedData.push({
                                id: `${itemIndex}-${keyIndex}`,
                                content_id: content?.content_id,
                                content_type_id: content?.content_type_id,
                                contenttypeid: content?.content_type_id,
                                assignmentId: assignmentId,
                                imageSource: content?.media_id,
                                thumbnail: content?.media_id?.startsWith('http')
                                    ? content.media_id
                                    : (content?.thumbnail_url || "/assets/images/no-img-found.webp"),
                                url: content?.url,
                                videoUrl: content?.url,
                                name: influencer?.name || "Influencer",
                                influencerUsername: influencer?.name,
                                profilePic: profilePictureUrl,
                                profilePictureUrl: profilePictureUrl,
                                profile_pic: influencer?.profile_picture || influencer?.influencer_profile_picture,
                                influencerId: influencerId,
                                influencerDp: influencer?.profile_picture || influencer?.influencer_profile_picture,
                                influencerHandle: platform,
                                identifier_id: item?.platforms_data?.[0]?.identifier_id,
                                platform_id: item?.platforms_data?.[0]?.platform_id,
                                platformId: item?.platforms_data?.[0]?.platform_id,
                                campaignId: campaignId,
                                date: new Date(item?.assignment_data?.date_added * 1000).toLocaleDateString(),
                                type: item?.campaign_data?.type,
                                // Analytics fields (may not be present for UGC)
                                views: 0,
                                view_count: 0,
                                likes: 0,
                                like_count: 0,
                                comments: 0,
                                comment_count: 0,
                                engagementRate: 0,
                                // Preserve raw data
                                ...item,
                                ...(item?.content_analytics?.[key] || {}) // Spread analytics if available
                            });
                        });
                    });

                    setContentList(mappedData);
                    setHasMore(false); // getAssignmentUser returns all at once
                    setIsLoading(false);
                    setIsLoadingMore(false);
                },
                (error) => {
                    console.error('ContentSection - UGC API Error:', error);
                    setContentList([]);
                    setIsLoading(false);
                    setIsLoadingMore(false);
                    setHasMore(false);
                },
                () => { },
                params
            );
        } else {
            // Social campaigns: Use getContentSyncedList API
            const params = { campaign_id: campaignId, start: (requestedPage - 1) * PAGE_SIZE, limit: PAGE_SIZE };
            Core.getContentSyncedList(
                (res, response) => {
                    console.log('ContentSection - API Response (page', requestedPage + '):', response);
                    const data = response?.response || [];

                    // Map synced data structure to match expected format for both Social and UGC cards
                    const mappedData = data.map((item, index) => {
                        const contentData = Object.values(item?.contents_deep_data || {})[0]?.content_data || {};
                        const analytics = Object.values(item?.content_analytics || {})[0] || {};
                        const influencer = item?.influencer_data || {};
                        const influencerId = influencer?.influencer_id || item?.assignment_data?.influencer_id;

                        const foundInfluencer = influencerId && influencerList?.length
                            ? influencerList.find(
                                (inf) => inf?.assignment_data?.influencer_id === influencerId || inf?.influencer_data?.influencer_id === influencerId
                            )
                            : null;

                        const profilePictureUrl =
                            resolveInfluencerProfilePicture(foundInfluencer?.influencer_data, influencerId) ||
                            resolveAvatarFromPayload(item, influencerId);


                        return {
                            ...item,
                            ...analytics,

                            id: index,
                            content_id: contentData?.content_id,
                            assignmentId: item?.assignment_data?.assignment_id,
                            thumbnail: analytics?.thumbnail || (contentData?.media_id?.startsWith('http')
                                ? contentData.media_id
                                : (contentData?.thumbnail_url || "/assets/images/no-img-found.webp")),
                            imageSource: contentData?.media_id, // For UGC
                            url: contentData?.url,
                            videoUrl: contentData?.url, // For UGC
                            name: influencer?.name || "Influencer",
                            influencerUsername: influencer?.name, // For UGC
                            profilePic: profilePictureUrl, // Using our mapped URL
                            profilePictureUrl: profilePictureUrl, // Explicit prop for clarity
                            profile_pic: influencer?.profile_picture || influencer?.influencer_profile_picture, // For UGC (legacy)
                            influencerId: influencerId,
                            influencerHandle: item?.platforms_data?.[0]?.identifier_id, // For UGC
                            contenttypeid: contentData?.content_type_id,
                            views: analytics?.view_count || analytics?.views || 0,
                            view_count: analytics?.view_count || analytics?.views || 0,
                            likes: analytics?.like_count || 0,
                            like_count: analytics?.like_count || 0,
                            comments: analytics?.comment_count || 0,
                            comment_count: analytics?.comment_count || 0,
                            engagementRate: analytics?.views > 0 ? ((analytics?.like_count + analytics?.comment_count) / analytics?.views) * 100 : 0,
                            platformId: item?.platforms_data?.platform_id,
                            date: new Date(item?.assignment_data?.date_added * 1000).toLocaleDateString(),
                        };
                    });

                    if (requestedPage === 1) {
                        setContentList(mappedData);
                    } else {
                        setContentList((prev) => [...prev, ...mappedData]);
                    }

                    if ((data || []).length < PAGE_SIZE) {
                        setHasMore(false);
                    } else {
                        setHasMore(true);
                    }

                    setPage((prev) => Math.max(prev, requestedPage) + 1);
                    setIsLoading(false);
                    setIsLoadingMore(false);
                },
                (error) => {
                    console.error('ContentSection - API Error:', error);
                    if (requestedPage === 1) {
                        setContentList([]);
                    }
                    setIsLoading(false);
                    setIsLoadingMore(false);
                    setHasMore(false);
                },
                () => { },
                params
            );
        }
    };

    const filteredContent = contentList;

    // Shimmer Loading Component
    const ShimmerCards = () => (
        <>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                <div key={index} className="content-grid-card" style={{ padding: '0' }}>
                    {/* Card Header Shimmer */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px',
                        borderBottom: '1px solid #f3f4f6'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <ShimmerBox width="36px" height="36px" borderRadius="50%" />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <ShimmerBox width="100px" height="14px" />
                                <ShimmerBox width="120px" height="10px" />
                            </div>
                        </div>
                        <ShimmerBox width="28px" height="28px" borderRadius="50%" />
                    </div>

                    {/* Card Image Shimmer */}
                    <div style={{ aspectRatio: '16/9', width: '100%' }}>
                        <ShimmerBox width="100%" height="100%" borderRadius="0" />
                    </div>

                    {/* Card Footer Shimmer */}
                    <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <ShimmerBox width="60px" height="22px" borderRadius="12px" />
                            <ShimmerBox width="80px" height="22px" borderRadius="12px" />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <ShimmerBox width="50px" height="16px" />
                            <ShimmerBox width="50px" height="16px" />
                            <ShimmerBox width="50px" height="16px" />
                            <ShimmerBox width="50px" height="16px" />
                        </div>
                    </div>
                </div>
            ))}
        </>
    );

    return (
        <div className="content-tab section-content" ref={sectionRef}>
            <div className="section-header">
                <h3>Content</h3>
                {!is_public && !isSharedView && isVisible !== undefined && (
                    <div className="section-controls">
                        <div className='sharing-status-container'>
                            <span className="sharing-status">Sharing status</span>
                            {isVisible ? (
                                <span className="visible-badge">Visible</span>
                            ) : (
                                <span className="hidden-badge">Hidden</span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Content Body */}
            {!isReportGenerated ? (
                <div style={{ padding: '40px', textAlign: 'center', borderRadius: '12px', minHeight: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', border: '1px dashed #e5e7eb', margin: '20px 0' }}>
                    <h3 style={{ fontSize: '16px', color: '#6B7280', fontWeight: 600 }}>Report not generated yet</h3>
                    <p style={{ color: '#9CA3AF', marginTop: '8px', fontSize: '14px' }}>Click 'Generate Report' to view this data.</p>
                </div>
            ) : (
                <>
                    {/* Content Count */}
                    {!contentGridLoading && filteredContent.length > 0 && (
                        <div style={{
                            marginBottom: '16px',
                            fontSize: '13px',
                            color: '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <span style={{
                                background: '#eff6ff',
                                color: '#3b82f6',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontWeight: 600,
                                fontSize: '12px'
                            }}>
                                {filteredContent.length}
                            </span>
                            <span>content pieces found</span>
                        </div>
                    )}

                    {contentGridLoading ? (
                        <div className="content-grid">
                            <ShimmerCards />
                        </div>
                    ) : (
                        <div className="content-grid">
                            {filteredContent.length > 0 ? (
                                filteredContent.map((item, index) => (
                                    isContentCampaign ? (
                                        <UGCContentCard key={index} item={item} />
                                    ) : (
                                        <ContentCard key={item.content_id || index} item={item} />
                                    )
                                ))
                            ) : (
                                <div className="no-content-message" style={{ gridColumn: '1 / -1' }}>
                                    <div className="fall-back-center-campaign">
                                        <FallBackBtn
                                            heading={"No Creatives Available"}
                                            title={"Try selecting a different platform or check back later"}
                                            widthIcon={100}
                                            heightIcon={100}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Load More Skeletons */}
                            {isLoadingMore && <ShimmerCards />}

                            {/* Load More Button */}
                            {hasMore && !contentGridLoading && !isLoadingMore && (
                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', width: '100%', gridColumn: '1 / -1' }}>
                                    <PrimaryCta
                                        invertSmall
                                        text="Load More"
                                        onClick={() => fetchContentList(page)}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}


        </div>
    );
};

export default ContentSection;
